import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { RootState } from "@/app/rootReducer";
import {
  selectActiveSessionCompanyName,
  selectIntacctId,
} from "@/modules/hub/features/UserAppSettings/selectors/userAppSettingsSelectors";
import { getUnifiedNavigation } from "@/navigation/unifiedNavigation";
import { NavigationItem } from "@/navigation/types";
import {
  ChevronRightIcon,
  ChevronUpDownIcon,
  BuildingOfficeIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarSeparator,
  SidebarGroupLabel,
  SidebarFooter,
} from "@/components/ui/sidebar";
import permissionsService from "@/services/permissionsService";
import { filterNavigationByAccess } from "@/utils/navigationHelpers";
import CompanySwitcherModal from "@/components/CompanySwitcherModal/CompanySwitcherModal";
import { toggleWideSidebarItem } from "@/app/globalSlices/wideSidebarSlice";
import { openFileManagerDialog } from "@/app/globalSlices/fileManagerDialogSlice";
import { cn } from "@/utils/utils";

// Animated Submenu Component
function AnimatedSubmenu({
  isOpen,
  children,
}: {
  isOpen: boolean;
  children: React.ReactNode;
}) {
  const [height, setHeight] = useState<number | undefined>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      setHeight(isOpen ? contentHeight : 0);
    }
  }, [isOpen, children]);

  return (
    <div
      className="overflow-hidden transition-all duration-300 ease-in-out"
      style={{ height: height }}
    >
      <div ref={contentRef}>
        <SidebarMenuSub className="mt-0 space-y-0 animate-in slide-in-from-top-1 duration-200">
          {children}
        </SidebarMenuSub>
      </div>
    </div>
  );
}

export default function WideSidebar() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const openItems = useSelector(
    (state: RootState) => state.wideSidebar.openItems,
  );

  const activeCompanyName = useSelector(selectActiveSessionCompanyName);
  const activeCompanyIntacctId = useSelector(selectIntacctId);
  const userAppSettings = useSelector(
    (state: RootState) => state.userAppSettings.userAppSettings,
  );

  const rawNav = getUnifiedNavigation();
  const { hasPermission, hasRole, hasCertainPathCompany } =
    permissionsService();

  const filteredNav = filterNavigationByAccess(
    rawNav,
    hasPermission,
    hasRole,
    hasCertainPathCompany(),
  );

  const companyAdminMenu = filteredNav.find(item => item.internalName === 'company-admin');

  const [companySwitcherOpen, setCompanySwitcherOpen] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    const impersonateEmployeeUuid = localStorage.getItem("impersonateEmployeeUuid");
    setIsImpersonating(!!impersonateEmployeeUuid);
  }, []);

  const permissions = userAppSettings?.permissions ?? [];
  const userName =
    userAppSettings?.firstName && userAppSettings?.lastName
      ? `${userAppSettings.firstName} ${userAppSettings.lastName}`
      : userAppSettings?.email || "User";

  const canOpenCompanySwitcher =
    hasRole("ROLE_SUPER_ADMIN") ||
    hasRole("ROLE_COACH") ||
    hasRole("ROLE_MARKETING");

  function handleToggleItem(itemKey: string) {
    dispatch(toggleWideSidebarItem(itemKey));
  }

  function isActiveRoute(href: string): boolean {
    if (href === "#") return false;

    // Exact match for /hub to prevent it from matching all /hub/* routes
    if (href === "/hub") {
      return location.pathname === "/hub";
    }

    // For all other routes, check exact match or if it's a parent route
    return (
      location.pathname === href || location.pathname.startsWith(href + "/")
    );
  }

  const handleSignOut = () => {
    navigate("/logout");
  };

  const handleExitImpersonation = () => {
    localStorage.removeItem("selectedCompanyUuid");
    localStorage.removeItem("impersonateEmployeeUuid");
    navigate(0);
  };

  function renderNavigation(items: NavigationItem[], depth = 0) {
    return items.map((item) => {
      const itemKey = item.internalName || item.name;
      const hasChildren = item.children && item.children.length > 0;
      const isOpen = !!openItems[itemKey];
      const isActive = item.href ? isActiveRoute(item.href) : false;

      if (hasChildren) {
        return (
          <SidebarMenuItem
            data-state={isOpen ? "open" : undefined}
            key={itemKey}
          >
            <SidebarMenuButton
              className={cn(
                depth === 0 && "font-medium text-sm",
                depth > 0 && "text-sm",
                depth === 0 ? "py-1.5 px-2" : "py-1 px-2 pl-6",
                isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
                "transition-all duration-200",
              )}
              data-state={isOpen ? "open" : undefined}
              onClick={() => handleToggleItem(itemKey)}
              size="default"
            >
              <div
                className={cn(
                  "flex h-3.5 w-3.5 items-center justify-center transition-transform duration-300",
                  depth === 0 &&
                    "bg-gradient-to-br from-primary/10 to-primary/5 rounded",
                  isOpen && "scale-110",
                )}
              >
                <item.icon
                  className={`${depth === 0 ? "h-2.5 w-2.5" : "h-3 w-3"} shrink-0`}
                />
              </div>
              <span className="flex-1">{item.name}</span>
              <ChevronRightIcon
                aria-hidden="true"
                className={cn(
                  "ml-auto h-3 w-3 transition-transform duration-300 ease-in-out",
                  isOpen && "rotate-90",
                )}
              />
            </SidebarMenuButton>
            <AnimatedSubmenu isOpen={isOpen}>
              {renderNavigation(item.children!, depth + 1)}
            </AnimatedSubmenu>
          </SidebarMenuItem>
        );
      }

      // Check if it's an external URL
      const isExternalUrl =
        item.href?.startsWith("http://") || item.href?.startsWith("https://");

      if (isExternalUrl) {
        return (
          <SidebarMenuItem key={itemKey}>
            <SidebarMenuButton
              asChild
              className={cn(
                depth === 0 && "font-medium text-sm",
                depth > 0 && "text-sm",
                depth === 0 ? "py-1.5 px-2" : "py-1 px-2 pl-6",
                isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
                "transition-all duration-200",
              )}
              size="default"
            >
              <a
                href={item.href}
                rel="noopener noreferrer"
                target="_blank"
                title={item.name}
              >
                <div
                  className={cn(
                    "flex h-3.5 w-3.5 items-center justify-center transition-transform duration-200",
                    depth === 0 &&
                      "bg-gradient-to-br from-primary/10 to-primary/5 rounded",
                    "hover:scale-110",
                  )}
                >
                  <item.icon
                    className={`${depth === 0 ? "h-2.5 w-2.5" : "h-3 w-3"} shrink-0`}
                  />
                </div>
                <span className="flex-1">{item.name}</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      }

      // Check if it's a dialog item
      if (item.isDialog) {
        return (
          <SidebarMenuItem key={itemKey}>
            <SidebarMenuButton
              className={cn(
                depth === 0 && "font-medium text-sm",
                depth > 0 && "text-sm",
                depth === 0 ? "py-1.5 px-2" : "py-1 px-2 pl-6",
                isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
                "transition-all duration-200",
              )}
              onClick={() => {
                if (item.dialogAction === "fileManager") {
                  dispatch(openFileManagerDialog());
                }
              }}
              size="default"
            >
              <div
                className={cn(
                  "flex h-3.5 w-3.5 items-center justify-center transition-transform duration-200",
                  depth === 0 &&
                    "bg-gradient-to-br from-primary/10 to-primary/5 rounded",
                  "hover:scale-110",
                )}
              >
                <item.icon
                  className={`${depth === 0 ? "h-2.5 w-2.5" : "h-3 w-3"} shrink-0`}
                />
              </div>
              <span className="flex-1">{item.name}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      }

      return (
        <SidebarMenuItem key={itemKey}>
          <SidebarMenuButton
            asChild
            className={cn(
              depth === 0 && "font-medium text-sm",
              depth > 0 && "text-sm",
              depth === 0 ? "py-1.5 px-2" : "py-1 px-2 pl-6",
              isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
              "transition-all duration-200",
            )}
            size="default"
          >
            <Link title={item.name} to={item.href ?? "#"}>
              <div
                className={cn(
                  "flex h-3.5 w-3.5 items-center justify-center transition-transform duration-200",
                  depth === 0 &&
                    "bg-gradient-to-br from-primary/10 to-primary/5 rounded",
                  "hover:scale-110",
                )}
              >
                <item.icon
                  className={`${depth === 0 ? "h-2.5 w-2.5" : "h-3 w-3"} shrink-0`}
                />
              </div>
              <span className="flex-1">{item.name}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    });
  }

  // Extract navigation sections directly from their children
  const hubSection = filteredNav.find((item) => item.internalName === "hub");
  const stochasticSection = filteredNav.find(
    (item) => item.internalName === "stochastic",
  );
  // For CertainPath companies, show "Internal Admin" (internalName: "admin")
  // For non-CertainPath companies, show "Admin" (internalName: "company-admin")
  const internalAdminSection = filteredNav.find(
    (item) => item.internalName === "admin",
  );
  const companyAdminSection = filteredNav.find(
    (item) => item.internalName === "company-admin",
  );
  // Use whichever admin section is available (filtered by access rules)
  const adminSection = internalAdminSection || companyAdminSection;

  // Get the direct children items
  const hubItems = hubSection?.children || [];
  const stochasticItems = stochasticSection?.children || [];
  const adminItems = adminSection?.children || [];

  return (
    <div className="flex-shrink-0 h-screen">
      <Sidebar
        className="h-full border-r-2"
        collapsible="offcanvas"
        side="left"
        variant="sidebar"
      >
        <SidebarHeader className="border-b-2 border-sidebar-border/50 bg-gradient-to-b from-sidebar to-sidebar/95">
          <div className="flex flex-col px-2 py-3">
            {canOpenCompanySwitcher ? (
              <button
                className="group flex w-full items-center justify-between rounded-md bg-sidebar-accent/50 p-2 text-left transition-all duration-200 hover:bg-sidebar-accent hover:shadow-md"
                onClick={() => setCompanySwitcherOpen(true)}
                type="button"
              >
                <div className="flex items-center space-x-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded bg-primary/10 transition-transform duration-200 group-hover:scale-110">
                    <BuildingOfficeIcon className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold leading-tight">
                      {activeCompanyName || "No Company"}
                    </div>
                    {activeCompanyIntacctId && (
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        ID: {activeCompanyIntacctId}
                      </div>
                    )}
                  </div>
                </div>
                <ChevronUpDownIcon
                  aria-hidden="true"
                  className="h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 group-hover:scale-110"
                />
              </button>
            ) : (
              <div className="flex items-center space-x-2 rounded-md bg-sidebar-accent/30 p-2">
                <div className="flex h-7 w-7 items-center justify-center rounded bg-primary/10">
                  <BuildingOfficeIcon className="h-3.5 w-3.5 text-primary" />
                </div>
                <div className="flex flex-col">
                  <div className="text-xs font-semibold leading-tight">
                    {activeCompanyName || "No Company"}
                  </div>
                  {activeCompanyIntacctId && (
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      ID: {activeCompanyIntacctId}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </SidebarHeader>

        <SidebarContent className="px-1 overflow-y-auto">
          {hubItems.length > 0 && (
            <SidebarGroup>
              <SidebarGroupLabel className="flex items-center gap-1.5 px-2 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <div className="h-px flex-1 bg-sidebar-border/50" />
                <span>Hub</span>
                <div className="h-px flex-1 bg-sidebar-border/50" />
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className="space-y-0">
                  {renderNavigation(hubItems)}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}

          {stochasticItems.length > 0 && (
            <>
              <SidebarSeparator className="my-1" />
              <SidebarGroup>
                <SidebarGroupLabel className="flex items-center gap-1.5 px-2 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <div className="h-px flex-1 bg-sidebar-border/50" />
                  <span>Stochastic</span>
                  <div className="h-px flex-1 bg-sidebar-border/50" />
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu className="space-y-0">
                    {renderNavigation(stochasticItems)}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </>
          )}

          {adminItems.length > 0 && (
            <>
              <SidebarSeparator className="my-1" />
              <SidebarGroup>
                <SidebarGroupLabel className="flex items-center gap-1.5 px-2 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <div className="h-px flex-1 bg-sidebar-border/50" />
                  <span>Administration</span>
                  <div className="h-px flex-1 bg-sidebar-border/50" />
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu className="space-y-0">
                    {renderNavigation(adminItems)}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </>
          )}
        </SidebarContent>

        <SidebarFooter className="border-t-2 border-sidebar-border/50 bg-gradient-to-t from-sidebar to-sidebar/95 p-1">
          <SidebarMenu>
            <SidebarMenuItem>
              <div className="flex items-center gap-2 px-2 py-1">
                <UserCircleIcon className="h-3.5 w-3.5 text-muted-foreground" />
                <div className="flex flex-col">
                  <span className="text-xs font-medium leading-tight">
                    {userName}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Logged in
                  </span>
                </div>
              </div>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                className="py-1 px-2 text-sm transition-all duration-200"
              >
                <Link to="/hub/settings">
                  <Cog6ToothIcon className="h-3.5 w-3.5" />
                  <span>Settings</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {isImpersonating && (
              <>
                <SidebarSeparator className="my-0.5" />
                <SidebarMenuItem>
                  <SidebarMenuButton
                    className="py-1 px-2 text-sm text-orange-600 hover:bg-orange-50 hover:text-orange-700 dark:hover:bg-orange-950/20 transition-all duration-200"
                    onClick={handleExitImpersonation}
                  >
                    <UserCircleIcon className="h-3.5 w-3.5" />
                    <span>Exit Impersonation</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </>
            )}
            <SidebarMenuItem>
              <SidebarMenuButton
                className="py-1 px-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/20 transition-all duration-200"
                onClick={handleSignOut}
              >
                <ArrowRightOnRectangleIcon className="h-3.5 w-3.5" />
                <span>Sign Out</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <CompanySwitcherModal
        onClose={() => setCompanySwitcherOpen(false)}
        open={companySwitcherOpen}
      />
    </div>
  );
}
